import dayjs from "dayjs";

const cleanValue = (value) => String(value || "").trim();

const normalizeBirthdateInput = (value) => {
  if (Array.isArray(value)) {
    return normalizeBirthdateInput(value[0]);
  }

  return cleanValue(value);
};

export const getProfileBirthdate = (profile) =>
  normalizeBirthdateInput(
    profile?.birthdate ||
      profile?.dateOfBirth ||
      profile?.date_of_birth ||
      profile?.birthday ||
      profile?.dob ||
      profile?.birthDate,
  );

export const getAgeFromBirthdate = (birthdate) => {
  const normalizedBirthdate = normalizeBirthdateInput(birthdate);
  if (!normalizedBirthdate) return null;

  const parsedBirthdate = dayjs(normalizedBirthdate);
  if (!parsedBirthdate.isValid()) return null;

  return dayjs().diff(parsedBirthdate, "year");
};

export const isUnder18FromBirthdate = (birthdate) => {
  const age = getAgeFromBirthdate(birthdate);
  return age != null && age < 18;
};

export const isUnder13FromBirthdate = (birthdate) => {
  const age = getAgeFromBirthdate(birthdate);
  return age != null && age < 13;
};

const hasExplicitMinorFlag = (profile) => {
  const candidates = [
    profile?.minor,
    profile?.isMinor,
    profile?.under18,
    profile?.isUnder18,
    profile?.profile?.minor,
    profile?.user?.minor,
  ];

  return candidates.some((value) => value === true || value === "true");
};

export const isMinorProfile = (profile) =>
  hasExplicitMinorFlag(profile) || isUnder18FromBirthdate(getProfileBirthdate(profile));

export const isUnder13Profile = (profile) =>
  isUnder13FromBirthdate(getProfileBirthdate(profile));

export const CONNECTION_SAFETY_MIN_AGE = 13;
export const CONNECTION_SAFETY_MAX_AGE = 18;
export const CONNECTION_SAFETY_AGE_GAP_YEARS = 4;

export const getProfileAge = (profile) =>
  getAgeFromBirthdate(getProfileBirthdate(profile));

export const isConnectionSafetyAge = (age) =>
  age != null &&
  age >= CONNECTION_SAFETY_MIN_AGE &&
  age <= CONNECTION_SAFETY_MAX_AGE;

export const shouldShowConnectionSafetyConfirmation = (
  currentUser,
  targetUser,
) => {
  const currentAge = getProfileAge(currentUser);
  const targetAge = getProfileAge(targetUser);

  if (!isConnectionSafetyAge(currentAge) || !isConnectionSafetyAge(targetAge)) {
    return false;
  }

  return Math.abs(currentAge - targetAge) >= CONNECTION_SAFETY_AGE_GAP_YEARS;
};

const normalizeNameForComparison = (value) =>
  cleanValue(value).toLowerCase().replace(/[^a-z0-9]/g, "");

export const isUsernameUsingFullName = (username, firstName, lastName) => {
  const normalizedUsername = normalizeNameForComparison(username);
  const normalizedFirstName = normalizeNameForComparison(firstName);
  const normalizedLastName = normalizeNameForComparison(lastName);

  if (!normalizedUsername || !normalizedFirstName || !normalizedLastName) {
    return false;
  }

  return (
    normalizedUsername === `${normalizedFirstName}${normalizedLastName}` ||
    (normalizedUsername.includes(normalizedFirstName) &&
      normalizedUsername.includes(normalizedLastName))
  );
};

export const getMinorSafeDisplayName = (
  profile,
  { isOwnProfile = false, fallbackUsername = "" } = {},
) => {
  const firstName = cleanValue(profile?.firstName);
  const lastName = cleanValue(profile?.lastName);
  const displayName = cleanValue(
    profile?.displayName || profile?.fullName || profile?.name,
  );
  const username = cleanValue(
    fallbackUsername ||
      profile?.username ||
      profile?.userName ||
      profile?.preferredUsername ||
      profile?.preferred_username,
  );

  if (isMinorProfile(profile) && !isOwnProfile) {
    if (firstName) return firstName;
    if (displayName) return cleanValue(displayName.split(" ")[0]);
    return "Kewordal member";
  }

  if (firstName || lastName) {
    return `${firstName} ${lastName}`.trim();
  }

  return displayName || username;
};

const extractSubjectName = (subject) => {
  if (!subject) return "";
  if (typeof subject === "string") return cleanValue(subject);

  return cleanValue(
    subject?.name ||
      subject?.title ||
      subject?.subjectName ||
      subject?.label ||
      subject?.displayName ||
      subject?.subject,
  );
};

export const getSelectedSubjectNames = (profile, limit = Infinity) => {
  const rawSources = [
    ...(Array.isArray(profile?.selectedSubjects) ? profile.selectedSubjects : []),
    ...(Array.isArray(profile?.roleDetails?.selectedSubjects)
      ? profile.roleDetails.selectedSubjects
      : []),
    ...(Array.isArray(profile?.roleDetails?.subjectNames)
      ? profile.roleDetails.subjectNames
      : []),
    ...(Array.isArray(profile?.subjectNames) ? profile.subjectNames : []),
    ...(Array.isArray(profile?.subjects) ? profile.subjects : []),
    ...(Array.isArray(profile?.subjectsSelected) ? profile.subjectsSelected : []),
    ...(Array.isArray(profile?.userSubjects) ? profile.userSubjects : []),
    ...(Array.isArray(profile?.subject) ? profile.subject : []),
    ...(Array.isArray(profile?.profile?.selectedSubjects)
      ? profile.profile.selectedSubjects
      : []),
    ...(Array.isArray(profile?.profile?.roleDetails?.selectedSubjects)
      ? profile.profile.roleDetails.selectedSubjects
      : []),
    ...(Array.isArray(profile?.profile?.roleDetails?.subjectNames)
      ? profile.profile.roleDetails.subjectNames
      : []),
    ...(Array.isArray(profile?.profile?.subjectNames)
      ? profile.profile.subjectNames
      : []),
    ...(Array.isArray(profile?.profile?.subjects) ? profile.profile.subjects : []),
    ...(Array.isArray(profile?.profile?.subjectsSelected)
      ? profile.profile.subjectsSelected
      : []),
    ...(Array.isArray(profile?.profile?.userSubjects)
      ? profile.profile.userSubjects
      : []),
    ...(Array.isArray(profile?.subjectIds) ? profile.subjectIds : []),
    ...(Array.isArray(profile?.selectedSubjectIds)
      ? profile.selectedSubjectIds
      : []),
  ];

  const uniqueNames = [];
  const seen = new Set();

  rawSources.forEach((subject) => {
    const name = extractSubjectName(subject);
    if (!name) return;

    const key = name.toLowerCase();
    if (seen.has(key)) return;

    seen.add(key);
    uniqueNames.push(name);
  });

  return uniqueNames.slice(0, limit);
};
